import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../components/ui/drawer';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Timer, Coins, ChevronRight, Building2, Clock, Trophy, Lock, ArrowLeft, Sparkles } from 'lucide-react';

const OPTION_COLORS = [
  { bg: 'linear-gradient(135deg, rgba(255,42,109,0.2), rgba(255,107,53,0.1))', border: '#FF2A6D', text: '#FF2A6D', glow: 'rgba(255,42,109,0.3)' },
  { bg: 'linear-gradient(135deg, rgba(5,217,232,0.2), rgba(83,109,254,0.1))', border: '#05D9E8', text: '#05D9E8', glow: 'rgba(5,217,232,0.3)' },
  { bg: 'linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,143,0,0.1))', border: '#FFD700', text: '#FFD700', glow: 'rgba(255,215,0,0.3)' },
  { bg: 'linear-gradient(135deg, rgba(1,255,195,0.2), rgba(0,200,83,0.1))', border: '#01FFC3', text: '#01FFC3', glow: 'rgba(1,255,195,0.3)' },
  { bg: 'linear-gradient(135deg, rgba(171,71,188,0.2), rgba(126,87,194,0.1))', border: '#AB47BC', text: '#AB47BC', glow: 'rgba(171,71,188,0.3)' },
  { bg: 'linear-gradient(135deg, rgba(255,107,53,0.2), rgba(255,143,0,0.1))', border: '#FF6B35', text: '#FF6B35', glow: 'rgba(255,107,53,0.3)' },
  { bg: 'linear-gradient(135deg, rgba(83,109,254,0.2), rgba(5,217,232,0.1))', border: '#536DFE', text: '#536DFE', glow: 'rgba(83,109,254,0.3)' },
];

export default function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const [gameSettings, setGameSettings] = useState(null);
  const [banner, setBanner] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [crossDigits, setCrossDigits] = useState([]);
  const [crossCombinations, setCrossCombinations] = useState([]);
  const [jodiSelected, setJodiSelected] = useState([]);
  const [pattiInput, setPattiInput] = useState('');
  const [hurfSide, setHurfSide] = useState(null);
  const [step, setStep] = useState('options');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [timers, setTimers] = useState({});
  const [countdowns, setCountdowns] = useState({});
  const [todayBets, setTodayBets] = useState({ total_amount: 0, total_count: 0, by_number: {} });

  const fetchData = useCallback(async () => {
    try {
      const [gsRes, stRes, cRes, btRes] = await Promise.all([
        api.get('/game/settings'), api.get('/settings'), api.get('/companies'), api.get('/game/my-bets-today')
      ]);
      setGameSettings(gsRes.data);
      setBanner(stRes.data.banner);
      setCompanies(cRes.data.companies || []);
      setTodayBets(btRes.data);
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(() => {
      api.get('/companies').then(r => setCompanies(r.data.companies || [])).catch(() => {});
      api.get('/game/my-bets-today').then(r => setTodayBets(r.data)).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const newCountdowns = {};
      const now = new Date();
      companies.forEach(c => {
        if (c.closing_time && c.betting_open) {
          try {
            const [h, m] = c.closing_time.split(':').map(Number);
            const closing = new Date();
            closing.setHours(h, m, 0, 0);
            // Cross-midnight: if bet_open_time > closing_time and we're past open time
            if (c.bet_open_time) {
              const [oh, om] = c.bet_open_time.split(':').map(Number);
              const openMin = oh * 60 + om;
              const closeMin = h * 60 + m;
              if (closeMin <= openMin && now.getHours() * 60 + now.getMinutes() >= openMin) {
                // We're after open time, closing is tomorrow
                closing.setDate(closing.getDate() + 1);
              }
            }
            const diffMs = closing.getTime() - now.getTime();
            newCountdowns[c.id] = diffMs > 0 ? Math.floor(diffMs / 1000) : 0;
          } catch { newCountdowns[c.id] = -1; }
        }
      });
      setCountdowns(newCountdowns);
    }, 1000);
    return () => clearInterval(interval);
  }, [companies]);

  useEffect(() => {
    const interval = setInterval(() => {
      const newTimers = {};
      companies.forEach(c => {
        if (c.timer_active && c.timer_end) {
          const diff = Math.max(0, Math.floor((new Date(c.timer_end).getTime() - Date.now()) / 1000));
          newTimers[c.id] = diff;
        }
      });
      setTimers(newTimers);
    }, 1000);
    return () => clearInterval(interval);
  }, [companies]);

  const formatCountdown = (totalSec) => {
    if (totalSec <= 0) return { h: '00', m: '00', s: '00', total: 0 };
    return {
      h: Math.floor(totalSec / 3600).toString().padStart(2, '0'),
      m: Math.floor((totalSec % 3600) / 60).toString().padStart(2, '0'),
      s: (totalSec % 60).toString().padStart(2, '0'),
      total: totalSec
    };
  };

  const getCountdownColor = (sec) => {
    if (sec <= 0) return '#FF2A6D';
    if (sec <= 300) return '#FF2A6D';
    if (sec <= 1800) return '#FFD700';
    return '#01FFC3';
  };

  // === OPTION CLICK ===
  const handleOptionClick = (opt) => {
    setSelectedOption(opt);
    setSelectedNumber(null);
    setCrossDigits([]);
    setCrossCombinations([]);
    setPattiInput('');
    setHurfSide(null);
    if (opt.input_type === 'single' && opt.name === 'Hurf') {
      setStep('hurf_side');
    } else {
      setStep('number');
    }
    setDrawerOpen(true);
  };

  // === NUMBER SELECTION based on input_type ===
  const handleNumberSelect = (num) => {
    setSelectedNumber(num);
    setStep('company');
  };

  const handleCrossDigitToggle = (digit) => {
    setCrossDigits(prev => {
      if (prev.includes(digit)) return prev.filter(d => d !== digit);
      if (prev.length >= 7) { toast.error('Cross: Maximum 7 digits allowed'); return prev; }
      return [...prev, digit];
    });
  };

  const confirmCross = () => {
    if (crossDigits.length < 2) { toast.error('Select at least 2 digits for Cross'); return; }
    const combos = [];
    crossDigits.forEach(a => crossDigits.forEach(b => combos.push(a * 10 + b)));
    const uniqueCombos = [...new Set(combos)];
    setCrossCombinations(uniqueCombos);
    setSelectedNumber(crossDigits[0] * 10 + crossDigits[1]);
    setStep('company');
  };

  const confirmPatti = () => {
    const num = parseInt(pattiInput);
    if (isNaN(num) || pattiInput.length !== 3) { toast.error('Enter a valid 3-digit number (000-999)'); return; }
    // Validate based on patti type
    if (selectedOption.input_type === 'single_patti') {
      const digits = pattiInput.split('');
      if (digits[0] === digits[1] || digits[1] === digits[2] || digits[0] === digits[2]) {
        toast.error('Single Patti: All 3 digits must be different!'); return;
      }
    } else if (selectedOption.input_type === 'double_patti') {
      const digits = pattiInput.split('');
      const hasPair = digits[0] === digits[1] || digits[1] === digits[2] || digits[0] === digits[2];
      const allSame = digits[0] === digits[1] && digits[1] === digits[2];
      if (!hasPair || allSame) { toast.error('Double Patti: Exactly 2 digits must be same!'); return; }
    }
    setSelectedNumber(num);
    setStep('company');
  };

  const handleCompanyClick = (company) => {
    if (company.result_declared && !company.betting_open) { toast.error(`${company.name} का रिज़ल्ट आ चुका है! अब बेट नहीं लगेगी।`); return; }
    if (!company.betting_open) { toast.error(`${company.name} का बेट टाइम खत्म! अब बेट नहीं लगेगी।`); return; }
    const timerExpired = company.timer_active && timers[company.id] !== undefined && timers[company.id] <= 0;
    if (timerExpired) { toast.error(`${company.name} का बेट टाइम खत्म! अब बेट नहीं लगेगी।`); return; }
    setSelectedCompany(company);
    setStep('bet');
  };

  const handleBet = async (amount) => {
    try {
      // Jodi bulk: place bet for each selected number
      if (selectedOption?.name === 'Jodi' && jodiSelected.length > 1) {
        let lastBalance = 0;
        for (const num of jodiSelected) {
          const res = await api.post('/game/bet', {
            number: num, option_index: 0, option_text: 'Jodi',
            amount: amount, company_id: selectedCompany.id, company_name: selectedCompany.name,
            hurf_side: null, cross_digits: null, cross_combinations: null,
          });
          lastBalance = res.data.new_balance;
        }
        toast.success(`${jodiSelected.length} Jodi bets placed! ${amount * jodiSelected.length} coins total. Balance: ${lastBalance}`);
      } else {
        const betAmount = selectedOption?.name === 'Hurf' && hurfSide === 'AB' ? amount * 2 : amount;
        const res = await api.post('/game/bet', {
          number: selectedNumber, option_index: 0, option_text: selectedOption.name,
          amount: betAmount, company_id: selectedCompany.id, company_name: selectedCompany.name,
          hurf_side: hurfSide,
          cross_digits: crossDigits.length > 0 ? crossDigits : null,
          cross_combinations: crossCombinations.length > 0 ? crossCombinations : null,
        });
        toast.success(`Bet placed! ${betAmount} coins. Balance: ${res.data.new_balance}`);
      }
      setDrawerOpen(false);
      setStep('options');
      setSelectedOption(null);
      setHurfSide(null);
      setCrossDigits([]);
      setCrossCombinations([]);
      setJodiSelected([]);
      refreshUser();
      api.get('/game/my-bets-today').then(r => setTodayBets(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const goBack = () => {
    if (step === 'bet') { setStep('company'); setSelectedCompany(null); }
    else if (step === 'company') { setStep('number'); setSelectedNumber(null); }
    else if (step === 'number' && selectedOption?.name === 'Hurf') { setStep('hurf_side'); }
    else if (step === 'number' || step === 'hurf_side') { setDrawerOpen(false); setStep('options'); setSelectedOption(null); setHurfSide(null); }
  };

  // === RENDER NUMBER INPUT based on option type ===
  const renderNumberInput = () => {
    if (!selectedOption) return null;
    const type = selectedOption.input_type;
    const isHurf = selectedOption.name === 'Hurf';

    // Helper: Get bet info for a number — only "placed" bets, with company tags
    const getBetMark = (n) => {
      const info = todayBets.by_number?.[n];
      if (!info || !info.bets) return null;
      // Filter only placed bets (not won/lost — result already done)
      const placedBets = info.bets.filter(b => b.status === 'placed');
      if (placedBets.length === 0) return null;
      return {
        total: placedBets.reduce((s, b) => s + b.amount, 0),
        companies: [...new Set(placedBets.map(b => b.company))],
        count: placedBets.length
      };
    };

    // Single digit (0-9) — also used by Hurf after side selection
    if (type === 'single') {
      return (
        <div className="space-y-4">
          {isHurf && hurfSide && (
            <div className="text-center mb-2">
              <span className="px-3 py-1 rounded-lg text-sm font-bold" style={{
                background: hurfSide === 'A' ? 'rgba(5,217,232,0.15)' : hurfSide === 'B' ? 'rgba(255,42,109,0.15)' : 'rgba(255,215,0,0.15)',
                color: hurfSide === 'A' ? '#05D9E8' : hurfSide === 'B' ? '#FF2A6D' : '#FFD700'
              }}>
                {hurfSide === 'A' ? 'Ander (Left Digit)' : hurfSide === 'B' ? 'Bahar (Right Digit)' : 'Both Sides (2x Amount)'}
              </span>
            </div>
          )}
          <div className="grid grid-cols-5 gap-3">
            {[0,1,2,3,4,5,6,7,8,9].map(n => (
              <button key={n} data-testid={`num-${n}`}
                className="aspect-square rounded-xl text-2xl font-black transition-all hover:scale-110 active:scale-95 fade-in-up"
                style={{
                  animationDelay: `${n * 40}ms`,
                  background: `linear-gradient(135deg, ${OPTION_COLORS[n % 7].glow}, transparent)`,
                  border: `2px solid ${OPTION_COLORS[n % 7].border}40`,
                  color: OPTION_COLORS[n % 7].text,
                  fontFamily: 'var(--font-heading)'
                }}
                onClick={() => handleNumberSelect(n)}>
                {n}
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Jodi (00-99) — Bulk select
    if (type === 'jodi') {
      const toggleJodi = (n) => {
        setJodiSelected(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n]);
      };
      const confirmJodi = () => {
        if (jodiSelected.length === 0) { toast.error('At least 1 number select करें'); return; }
        if (jodiSelected.length === 1) {
          setSelectedNumber(jodiSelected[0]);
          setCrossCombinations([]);
        } else {
          setSelectedNumber(jodiSelected[0]);
          setCrossCombinations(jodiSelected);
        }
        setStep('company');
      };
      return (
        <div className="space-y-3">
          <p className="text-xs text-center" style={{ color: 'var(--neon-text-muted)' }}>
            Numbers touch करके select करो (1 to 100) — bulk bet लगा सकते हो
          </p>
          <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-1.5">
            {Array.from({ length: 100 }, (_, i) => i).map(n => {
              const isSelected = jodiSelected.includes(n);
              const betMark = getBetMark(n);
              const colorIdx = n % 7;
              return (
                <button key={n} data-testid={`num-${n}`}
                  className="aspect-square rounded-lg text-sm font-black transition-all hover:scale-105 active:scale-95 fade-in-up relative"
                  style={{
                    animationDelay: `${(n % 25) * 20}ms`,
                    background: isSelected ? OPTION_COLORS[colorIdx].glow : betMark ? `${OPTION_COLORS[colorIdx].glow}40` : 'rgba(255,255,255,0.03)',
                    border: isSelected ? `3px solid ${OPTION_COLORS[colorIdx].border}` : betMark ? `2px solid ${OPTION_COLORS[colorIdx].border}` : '1px solid rgba(255,255,255,0.08)',
                    color: OPTION_COLORS[colorIdx].text,
                    fontFamily: 'var(--font-heading)',
                    boxShadow: isSelected ? `0 0 12px ${OPTION_COLORS[colorIdx].glow}` : 'none'
                  }}
                  onClick={() => toggleJodi(n)}>
                  {n.toString().padStart(2, '0')}
                  {betMark && !isSelected && (
                    <span className="absolute -top-1 -right-1 text-[8px] font-black px-1 rounded-full"
                      style={{ background: 'var(--neon-secondary)', color: '#fff', minWidth: '14px', textAlign: 'center' }}>
                      {betMark.companies.length > 1 ? `${betMark.companies.length}C` : betMark.companies[0]?.slice(0, 3)}
                    </span>
                  )}
                  {isSelected && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[8px]"
                      style={{ background: '#01FFC3', color: '#000' }}>✓</span>
                  )}
                </button>
              );
            })}
          </div>
          {jodiSelected.length > 0 && (
            <div className="text-center fade-in-up p-3 rounded-xl" style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.2)' }}>
              <p className="text-sm mb-1 font-bold" style={{ color: '#FFD700' }}>
                {jodiSelected.length} number{jodiSelected.length > 1 ? 's' : ''} selected
              </p>
              <div className="flex flex-wrap gap-1 justify-center mb-2 max-h-16 overflow-y-auto">
                {jodiSelected.map(n => (
                  <span key={n} className="px-1.5 py-0.5 rounded text-xs font-bold" style={{ background: 'rgba(255,215,0,0.15)', color: '#FFD700' }}>
                    {n.toString().padStart(2, '0')}
                  </span>
                ))}
              </div>
              <button data-testid="jodi-confirm" onClick={confirmJodi}
                className="btn-neon-primary px-8 py-2 text-sm font-bold">
                Confirm Jodi ({jodiSelected.length} numbers)
              </button>
            </div>
          )}
        </div>
      );
    }

    // Cross (pick 2-7 digits from 0-9)
    if (type === 'cross') {
      // Calculate live combinations
      const liveCombos = crossDigits.length >= 2
        ? [...new Set(crossDigits.flatMap(a => crossDigits.map(b => a * 10 + b)))]
        : [];
      return (
        <div className="space-y-4">
          <p className="text-xs text-center" style={{ color: 'var(--neon-text-muted)' }}>
            2 to 7 digits select karo — Cross automatically banega ({crossDigits.length} selected)
          </p>
          <div className="grid grid-cols-5 gap-3">
            {[0,1,2,3,4,5,6,7,8,9].map(n => {
              const isSelected = crossDigits.includes(n);
              return (
                <button key={n} data-testid={`cross-${n}`}
                  className="aspect-square rounded-xl text-2xl font-black transition-all hover:scale-110 active:scale-95 fade-in-up"
                  style={{
                    animationDelay: `${n * 40}ms`,
                    background: isSelected ? OPTION_COLORS[n % 7].glow : `rgba(255,255,255,0.03)`,
                    border: isSelected ? `3px solid ${OPTION_COLORS[n % 7].border}` : `1px solid rgba(255,255,255,0.1)`,
                    color: isSelected ? OPTION_COLORS[n % 7].text : 'var(--neon-text-muted)',
                    fontFamily: 'var(--font-heading)',
                    boxShadow: isSelected ? `0 0 20px ${OPTION_COLORS[n % 7].glow}` : 'none',
                    transform: isSelected ? 'scale(1.1)' : ''
                  }}
                  onClick={() => handleCrossDigitToggle(n)}>
                  {n}
                </button>
              );
            })}
          </div>
          {liveCombos.length > 0 && (
            <div className="text-center fade-in-up">
              <p className="text-sm mb-2" style={{ color: 'var(--neon-text-secondary)' }}>
                Cross Combinations ({liveCombos.length}):
              </p>
              <div className="flex flex-wrap gap-2 justify-center mb-3">
                {liveCombos.map(c => (
                  <span key={c} className="px-2.5 py-1 rounded-lg font-black text-sm" style={{ background: 'rgba(255,215,0,0.15)', border: '1px solid rgba(255,215,0,0.3)', color: 'var(--neon-accent)', fontFamily: 'var(--font-heading)' }}>
                    {c.toString().padStart(2, '0')}
                  </span>
                ))}
              </div>
              <button data-testid="cross-confirm" onClick={confirmCross}
                className="btn-neon-primary px-8 py-2 text-sm font-bold">
                Confirm Cross ({liveCombos.length} combinations)
              </button>
            </div>
          )}
        </div>
      );
    }

    // Triple Patti (000, 111, ..., 999)
    if (type === 'triple_patti') {
      return (
        <div className="grid grid-cols-5 gap-3">
          {[0,1,2,3,4,5,6,7,8,9].map(n => {
            const tripleNum = parseInt(`${n}${n}${n}`);
            return (
              <button key={n} data-testid={`num-${tripleNum}`}
                className="aspect-square rounded-xl text-xl font-black transition-all hover:scale-110 active:scale-95 fade-in-up"
                style={{
                  animationDelay: `${n * 40}ms`,
                  background: `linear-gradient(135deg, ${OPTION_COLORS[n % 7].glow}, transparent)`,
                  border: `2px solid ${OPTION_COLORS[n % 7].border}40`,
                  color: OPTION_COLORS[n % 7].text,
                  fontFamily: 'var(--font-heading)'
                }}
                onClick={() => handleNumberSelect(tripleNum)}>
                {`${n}${n}${n}`}
              </button>
            );
          })}
        </div>
      );
    }

    // Single Patti / Double Patti (type 3 digits)
    if (type === 'single_patti' || type === 'double_patti') {
      const hint = type === 'single_patti'
        ? 'All 3 digits must be different (e.g., 123, 456)'
        : 'Exactly 2 digits must be same (e.g., 112, 558)';
      return (
        <div className="space-y-4">
          <p className="text-xs text-center" style={{ color: 'var(--neon-text-muted)' }}>{hint}</p>
          <div className="flex justify-center">
            <Input data-testid="patti-input"
              className="neon-input text-center text-3xl font-black tracking-[0.5em] w-48"
              style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-accent)', letterSpacing: '0.3em' }}
              type="text" maxLength={3} placeholder="000"
              value={pattiInput} onChange={(e) => setPattiInput(e.target.value.replace(/\D/g, '').slice(0, 3))} />
          </div>
          {pattiInput.length === 3 && (
            <div className="text-center fade-in-up">
              <button data-testid="patti-confirm" onClick={confirmPatti}
                className="btn-neon-primary px-8 py-2 text-sm font-bold">
                Confirm {selectedOption.name}
              </button>
            </div>
          )}
        </div>
      );
    }

    // Default fallback: 0-9
    return (
      <div className="grid grid-cols-5 gap-3">
        {[0,1,2,3,4,5,6,7,8,9].map(n => (
          <button key={n} data-testid={`num-${n}`}
            className="aspect-square rounded-xl text-2xl font-black transition-all hover:scale-110 fade-in-up"
            style={{
              animationDelay: `${n * 40}ms`,
              background: `rgba(255,255,255,0.03)`,
              border: `1px solid rgba(255,255,255,0.1)`,
              color: 'var(--neon-text)',
              fontFamily: 'var(--font-heading)'
            }}
            onClick={() => handleNumberSelect(n)}>
            {n}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen pb-28" style={{ background: 'var(--neon-bg)' }}>
      {banner?.active && banner?.text && (
        <div data-testid="offer-banner" className="overflow-hidden py-2 px-4"
          style={{ background: 'linear-gradient(90deg, var(--neon-secondary), var(--neon-primary))', color: '#000' }}>
          <div className="marquee-text font-bold text-sm">{banner.text} &nbsp;&nbsp;&nbsp; {banner.text}</div>
        </div>
      )}

      <div className="p-4 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-text)' }}>
              Choose Your Game
            </h2>
            <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Select an option to start betting</p>
          </div>
          <div className="flex items-center gap-3">
            {todayBets.total_count > 0 && (
              <div data-testid="today-bet-total" className="flex items-center gap-1 px-3 py-1 rounded-lg"
                style={{ background: 'rgba(255,42,109,0.1)', border: '1px solid rgba(255,42,109,0.2)' }}>
                <span className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Today</span>
                <span className="font-bold text-sm" style={{ color: 'var(--neon-secondary)' }}>{todayBets.total_amount}</span>
              </div>
            )}
            <div data-testid="wallet-balance-header" className="flex items-center gap-1 px-3 py-1 rounded-lg"
              style={{ background: 'var(--neon-elevated)', color: 'var(--neon-accent)' }}>
              <Coins size={14} />
              <span className="font-bold text-sm">{user?.wallet_balance || 0}</span>
            </div>
          </div>
        </div>

        {/* === OPTION CARDS - Attractive Animated === */}
        {gameSettings?.options && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {gameSettings.options.map((opt, idx) => {
              const color = OPTION_COLORS[idx % OPTION_COLORS.length];
              return (
                <button key={idx} data-testid={`option-card-${opt.name}`}
                  className="option-card-animate relative overflow-hidden rounded-2xl p-5 text-left transition-all hover:scale-[1.04] active:scale-95"
                  style={{
                    background: color.bg,
                    border: `2px solid ${color.border}50`,
                    boxShadow: `0 4px 24px ${color.glow}`,
                    animationDelay: `${idx * 80}ms`
                  }}
                  onClick={() => handleOptionClick(opt)}>
                  {/* Shimmer effect */}
                  <div className="option-shimmer" style={{ background: `linear-gradient(120deg, transparent, ${color.border}15, transparent)` }} />
                  <Sparkles size={16} style={{ color: `${color.border}80`, marginBottom: '6px' }} />
                  <span className="font-black text-base block" style={{ color: color.text, fontFamily: 'var(--font-heading)', letterSpacing: '0.5px' }}>
                    {opt.name}
                  </span>
                  <span className="text-[10px] block mt-1 uppercase tracking-widest" style={{ color: `${color.text}90` }}>
                    {opt.multiplier}x
                  </span>
                  {/* Input type hint */}
                  <span className="text-[9px] block mt-1" style={{ color: 'var(--neon-text-muted)' }}>
                    {opt.input_type === 'jodi' && '00-99'}
                    {opt.input_type === 'single' && '0-9'}
                    {opt.input_type === 'cross' && '2 digits'}
                    {opt.input_type === 'single_patti' && '3 digits'}
                    {opt.input_type === 'double_patti' && '3 digits'}
                    {opt.input_type === 'triple_patti' && '000-999'}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Companies with Countdown (always visible) */}
        <div className="mt-6">
          <h3 className="text-sm font-bold mb-3" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-text-secondary)' }}>
            <Clock size={14} className="inline mr-1" /> Markets
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {companies.map(c => {
              const cd = countdowns[c.id];
              const cdF = cd !== undefined && cd >= 0 ? formatCountdown(cd) : null;
              const cdColor = cd !== undefined ? getCountdownColor(cd) : '#01FFC3';
              const isPulsing = cd !== undefined && cd > 0 && cd <= 300;
              return (
                <div key={c.id} data-testid={`market-${c.id}`} className="p-3 rounded-xl flex items-center justify-between"
                  style={{ background: 'var(--neon-elevated)', border: '1px solid rgba(255,255,255,0.04)' }}>
                  <div className="flex items-center gap-2">
                    <Building2 size={16} style={{ color: c.result_declared ? 'var(--neon-secondary)' : 'var(--neon-accent)' }} />
                    <div>
                      <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>{c.name}</p>
                      {c.closing_time && <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>Close: {c.closing_time}</p>}
                    </div>
                  </div>
                  <div>
                    {c.result_declared && !c.betting_open && (
                      <div className="flex flex-col items-end gap-0.5">
                        <div className="flex items-center gap-1">
                          <Trophy size={12} style={{ color: 'var(--neon-accent)' }} />
                          <span className="font-bold text-sm" style={{ color: 'var(--neon-accent)' }}>{c.winning_number}</span>
                        </div>
                        {c.betting_not_started && c.bet_open_time && (
                          <span className="text-[10px] font-bold flex items-center gap-1" style={{ color: 'var(--neon-primary)' }}>
                            <Clock size={8} /> Opens {c.bet_open_time}
                          </span>
                        )}
                      </div>
                    )}
                    {c.result_declared && c.betting_open && (
                      <div className="flex flex-col items-end gap-0.5">
                        <div className="flex items-center gap-1">
                          <Trophy size={12} style={{ color: 'var(--neon-accent)' }} />
                          <span className="font-bold text-sm" style={{ color: 'var(--neon-accent)' }}>{c.winning_number}</span>
                        </div>
                        {(() => {
                          const timerSec = (c.timer_active && timers[c.id] !== undefined) ? timers[c.id] : null;
                          const closeSec = (cd !== undefined && cd >= 0) ? cd : null;
                          const showSec = timerSec !== null ? timerSec : closeSec;
                          if (showSec !== null && showSec > 0) {
                            const f = formatCountdown(showSec);
                            const col = getCountdownColor(showSec);
                            return <span className="font-bold text-[10px] tabular-nums" style={{ color: col, fontFamily: 'var(--font-heading)' }}>{f.h}:{f.m}:{f.s}</span>;
                          }
                          return null;
                        })()}
                      </div>
                    )}
                    {!c.result_declared && c.betting_not_started && (
                      <span className="text-xs font-bold flex items-center gap-1" style={{ color: 'var(--neon-primary)' }}>
                        <Clock size={10} /> Opens {c.bet_open_time}
                      </span>
                    )}
                    {!c.result_declared && !c.betting_open && !c.betting_not_started && (
                      <span className="text-xs font-bold flex items-center gap-1" style={{ color: 'var(--neon-secondary)' }}><Lock size={10} /> Bet Closed</span>
                    )}
                    {!c.result_declared && c.betting_open && (() => {
                      // Show timer countdown OR closing_time countdown
                      const timerSec = (c.timer_active && timers[c.id] !== undefined) ? timers[c.id] : null;
                      const closeSec = (cd !== undefined && cd >= 0) ? cd : null;
                      const showSec = timerSec !== null ? timerSec : closeSec;
                      if (showSec !== null && showSec > 0) {
                        const f = formatCountdown(showSec);
                        const col = getCountdownColor(showSec);
                        const pulse = showSec <= 300;
                        return (
                          <div className={`flex items-center gap-0.5 ${pulse ? 'animate-pulse' : ''}`}>
                            <span className="font-black text-sm tabular-nums" style={{ color: col, fontFamily: 'var(--font-heading)' }}>{f.h}:{f.m}:{f.s}</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* === DRAWER === */}
      <Drawer open={drawerOpen} onOpenChange={(open) => { setDrawerOpen(open); if (!open) { setStep('options'); setSelectedOption(null); } }}>
        <DrawerContent className="border-t" style={{ background: 'var(--neon-surface)', borderColor: 'rgba(255,255,255,0.1)' }}>
          <DrawerHeader>
            <DrawerTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-primary)' }}>
              <button data-testid="drawer-back" onClick={goBack} className="p-1 rounded hover:bg-white/5"><ArrowLeft size={18} /></button>
              {step === 'hurf_side' && (
                <span className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-lg text-xs font-bold" style={{ background: OPTION_COLORS[(gameSettings?.options?.indexOf(selectedOption) || 0) % 7].glow, color: OPTION_COLORS[(gameSettings?.options?.indexOf(selectedOption) || 0) % 7].text }}>
                    {selectedOption?.name}
                  </span>
                  <ChevronRight size={12} />
                  <span className="text-sm" style={{ color: 'var(--neon-accent)' }}>Select Side</span>
                </span>
              )}
              {step === 'number' && (
                <span className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-lg text-xs font-bold" style={{ background: OPTION_COLORS[(gameSettings?.options?.indexOf(selectedOption) || 0) % 7].glow, color: OPTION_COLORS[(gameSettings?.options?.indexOf(selectedOption) || 0) % 7].text }}>
                    {selectedOption?.name}
                  </span>
                  <ChevronRight size={12} />
                  <span className="text-sm" style={{ color: 'var(--neon-accent)' }}>Choose Number</span>
                </span>
              )}
              {step === 'company' && (
                <span className="flex items-center gap-2 flex-wrap">
                  <span className="px-2 py-0.5 rounded-lg text-xs font-bold" style={{ background: OPTION_COLORS[(gameSettings?.options?.indexOf(selectedOption) || 0) % 7].glow, color: OPTION_COLORS[(gameSettings?.options?.indexOf(selectedOption) || 0) % 7].text }}>
                    {selectedOption?.name}
                  </span>
                  <ChevronRight size={12} />
                  <span style={{ color: 'var(--neon-accent)' }}>#{selectedNumber}</span>
                  <ChevronRight size={12} />
                  <span className="text-sm" style={{ color: 'var(--neon-text-muted)' }}>Select Market</span>
                </span>
              )}
              {step === 'bet' && (
                <span className="flex items-center gap-2 flex-wrap">
                  <span className="px-2 py-0.5 rounded-lg text-xs font-bold" style={{ background: OPTION_COLORS[(gameSettings?.options?.indexOf(selectedOption) || 0) % 7].glow, color: OPTION_COLORS[(gameSettings?.options?.indexOf(selectedOption) || 0) % 7].text }}>
                    {selectedOption?.name}
                  </span>
                  <ChevronRight size={12} />
                  <span style={{ color: 'var(--neon-accent)' }}>#{selectedNumber}</span>
                  <ChevronRight size={12} />
                  <span style={{ color: 'var(--neon-primary)' }}>{selectedCompany?.name}</span>
                </span>
              )}
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6 max-h-[60vh] overflow-y-auto">
            {/* Hurf Side Selection */}
            {step === 'hurf_side' && (
              <div className="space-y-4">
                <p className="text-sm text-center" style={{ color: 'var(--neon-text-muted)' }}>
                  Select side for Hurf bet
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { side: 'A', label: 'Ander', desc: 'Left digit match', color: '#05D9E8', bg: 'rgba(5,217,232,0.15)' },
                    { side: 'B', label: 'Bahar', desc: 'Right digit match', color: '#FF2A6D', bg: 'rgba(255,42,109,0.15)' },
                    { side: 'AB', label: 'Both', desc: '2x amount, both sides', color: '#FFD700', bg: 'rgba(255,215,0,0.15)' },
                  ].map(s => (
                    <button key={s.side} data-testid={`hurf-side-${s.side}`}
                      className="p-5 rounded-2xl text-center transition-all hover:scale-105 active:scale-95 option-card-animate"
                      style={{
                        background: s.bg, border: `2px solid ${s.color}50`,
                        boxShadow: `0 4px 20px ${s.bg}`,
                      }}
                      onClick={() => { setHurfSide(s.side); setStep('number'); }}>
                      <span className="text-2xl font-black block mb-1" style={{ color: s.color, fontFamily: 'var(--font-heading)' }}>{s.side}</span>
                      <span className="text-sm font-bold block" style={{ color: s.color }}>{s.label}</span>
                      <span className="text-[10px] block mt-1" style={{ color: 'var(--neon-text-muted)' }}>{s.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Number Selection */}
            {step === 'number' && renderNumberInput()}

            {/* Company Selection */}
            {step === 'company' && (
              <div className="space-y-2">
                {companies.filter(c => c.betting_open).length === 0 && (
                  <p className="text-center py-4" style={{ color: 'var(--neon-text-muted)' }}>No open markets</p>
                )}
                {companies.map(c => {
                  const timerExpired = c.timer_active && timers[c.id] !== undefined && timers[c.id] <= 0;
                  const isClosed = !c.betting_open || timerExpired;
                  if (isClosed) return null;
                  const cd = countdowns[c.id];
                  const cdF = cd !== undefined && cd >= 0 ? formatCountdown(cd) : null;
                  const cdColor = cd !== undefined ? getCountdownColor(cd) : '#01FFC3';
                  // Check if user already bet this number on this company
                  const numInfo = todayBets.by_number?.[selectedNumber];
                  const existingBet = numInfo?.bets?.find(b => b.company_id === c.id && b.status === 'placed');
                  return (
                    <button key={c.id} data-testid={`company-${c.id}`}
                      className="w-full p-4 rounded-xl text-left transition-all hover:scale-[1.01]"
                      style={{ background: 'var(--neon-elevated)', border: existingBet ? '2px solid var(--neon-secondary)' : '1px solid rgba(255,255,255,0.06)' }}
                      onClick={() => handleCompanyClick(c)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Building2 size={18} style={{ color: existingBet ? 'var(--neon-secondary)' : 'var(--neon-accent)' }} />
                          <div>
                            <p className="font-bold" style={{ color: 'var(--neon-text)' }}>{c.name}</p>
                            {existingBet && (
                              <p className="text-[10px]" style={{ color: 'var(--neon-secondary)' }}>
                                Already bet ₹{existingBet.amount} ({existingBet.option})
                              </p>
                            )}
                          </div>
                        </div>
                        {cdF && cdF.total > 0 && (
                          <span className="font-bold text-sm tabular-nums" style={{ color: cdColor, fontFamily: 'var(--font-heading)' }}>
                            {cdF.h}:{cdF.m}:{cdF.s}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Bet Amount */}
            {step === 'bet' && gameSettings?.bet_amounts && (
              <div>
                {selectedOption?.name === 'Hurf' && hurfSide === 'AB' && (
                  <p className="text-xs text-center mb-3 p-2 rounded-lg" style={{ background: 'rgba(255,215,0,0.1)', color: '#FFD700' }}>
                    AB = Both sides! Amount will be 2x (e.g. 100 = 200 total)
                  </p>
                )}
                {selectedOption?.name === 'Cross' && crossCombinations.length > 0 && (
                  <div className="mb-3 p-3 rounded-xl" style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.2)' }}>
                    <p className="text-xs mb-2 font-bold" style={{ color: '#FFD700' }}>Cross: {crossCombinations.length} combinations</p>
                    <div className="flex flex-wrap gap-1.5 justify-center mb-2">
                      {crossCombinations.map(c => (
                        <span key={c} className="px-2 py-0.5 rounded text-xs font-bold" style={{ background: 'rgba(255,215,0,0.15)', color: '#FFD700' }}>
                          {c.toString().padStart(2, '0')}
                        </span>
                      ))}
                    </div>
                    <p className="text-[10px] text-center" style={{ color: 'var(--neon-text-muted)' }}>
                      Amount x {crossCombinations.length} combos = Total deducted
                    </p>
                  </div>
                )}
                {selectedOption?.name === 'Jodi' && jodiSelected.length > 1 && (
                  <div className="mb-3 p-3 rounded-xl" style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.2)' }}>
                    <p className="text-xs mb-2 font-bold" style={{ color: '#FFD700' }}>Jodi: {jodiSelected.length} numbers</p>
                    <div className="flex flex-wrap gap-1.5 justify-center mb-2 max-h-16 overflow-y-auto">
                      {jodiSelected.map(n => (
                        <span key={n} className="px-2 py-0.5 rounded text-xs font-bold" style={{ background: 'rgba(255,215,0,0.15)', color: '#FFD700' }}>
                          {n.toString().padStart(2, '0')}
                        </span>
                      ))}
                    </div>
                    <p className="text-[10px] text-center" style={{ color: 'var(--neon-text-muted)' }}>
                      Amount x {jodiSelected.length} numbers = Total deducted
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-4 gap-3">
                  {gameSettings.bet_amounts.map((amt) => {
                    const bulkCount = selectedOption?.name === 'Cross' && crossCombinations.length > 0
                      ? crossCombinations.length
                      : selectedOption?.name === 'Jodi' && jodiSelected.length > 1
                        ? jodiSelected.length
                        : 1;
                    const totalAmt = selectedOption?.name === 'Hurf' && hurfSide === 'AB' ? amt * 2 : amt * bulkCount;
                    return (
                      <button key={amt} data-testid={`bet-amount-${amt}`} className="bet-btn flex flex-col items-center" onClick={() => handleBet(amt)}>
                        <span>{amt}</span>
                        {totalAmt !== amt && <span className="text-[9px] opacity-70">({totalAmt})</span>}
                      </button>
                    );
                  })}
                </div>
                {/* Manual Amount Input */}
                <div className="mt-3 flex gap-2">
                  <input data-testid="manual-bet-input" type="number" placeholder="Manual amount"
                    className="flex-1 p-2.5 rounded-xl text-sm font-bold text-center"
                    style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}
                    min={1}
                    onKeyDown={(e) => { if (e.key === 'Enter') { const v = parseInt(e.target.value); if (v > 0) { handleBet(v); e.target.value = ''; } } }}
                    id="manual-bet-amount" />
                  <button data-testid="manual-bet-submit" className="btn-neon-primary px-5 rounded-xl text-sm font-bold"
                    onClick={() => { const el = document.getElementById('manual-bet-amount'); const v = parseInt(el?.value); if (v > 0) { handleBet(v); el.value = ''; } else { toast.error('Amount डालो'); } }}>
                    Bet
                  </button>
                </div>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
